/**
 * Hook that maintains the per-defect Projected ERTS map for an event modal.
 *
 * The ERTS Prediction Pipeline scores each defect on an event (refreshed every
 * few minutes). When the modal opens for an existing event, this hook seeds the
 * map from any persisted values, then overlays each defect's own projection from
 * the pipeline. When no per-defect breakdown is available it falls back to the
 * event-level latest across every defect. The projection is read-only in the UI;
 * manual ERTS/RTS entry is unaffected.
 */
import { useEffect, useState } from 'react';
import { UnscheduledEvent } from '../../types';
import { ErtsPrediction, fetchErtsPrediction } from '../../services/ertsPredictionService';

/**
 * Converts an ISO-8601 Zulu timestamp (e.g. `2026-06-02T18:55:17Z`) into the
 * `YYYY-MM-DD HH:MM` shape the defect Projected ERTS inputs consume. Returns an
 * empty string for missing or unparseable input.
 */
export function formatProjectedErts(predictedReturnZulu: string): string {
  if (!predictedReturnZulu) return '';
  const match = predictedReturnZulu.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (!match) return '';
  return `${match[1]} ${match[2]}`;
}

/**
 * Return shape for {@link useProjectedErts}.
 */
export interface UseProjectedErtsResult {
  projectedErts: Record<number, string>;
  setProjectedErts: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

/**
 * Builds the next projected-ERTS map from a pipeline prediction. A per-defect
 * breakdown replaces the map outright, since it is authoritative for every
 * defect. Without one, the event-level latest is applied across `defectIds` over
 * the existing map. Returns `previous` unchanged when nothing usable is present.
 */
function mergeProjection(
  previous: Record<number, string>,
  prediction: ErtsPrediction,
  defectIds: number[],
): Record<number, string> {
  const defectEntries = Object.entries(prediction.defectPredictions || {});
  if (defectEntries.length > 0) {
    // Apply each defect's own projected return time.
    const next: Record<number, string> = {};
    for (const [defectId, defectPrediction] of defectEntries) {
      const value = formatProjectedErts(defectPrediction.predictedReturnZulu);
      if (value) next[Number(defectId)] = value;
    }
    return next;
  }
  // Fall back to the event-level latest across every defect.
  const value = formatProjectedErts(prediction.predictedReturnZulu);
  if (!value) return previous;
  const next: Record<number, string> = { ...previous };
  for (const id of defectIds) {
    next[id] = value;
  }
  return next;
}

/**
 * Manages projected-ERTS state for the modal and loads the pipeline projection
 * when it opens for an existing event.
 */
export function useProjectedErts(
  isOpen: boolean,
  event: UnscheduledEvent | null,
  projectedErtsOverlay?: Record<number, string>,
): UseProjectedErtsResult {
  const [projectedErts, setProjectedErts] = useState<Record<number, string>>({});

  // Prefer the live active-list projection; full-record hydration is schema-pure.
  useEffect(() => {
    if (!isOpen) return;
    setProjectedErts(projectedErtsOverlay ?? event?.projectedErts ?? {});
  }, [isOpen, event, projectedErtsOverlay]);

  // Refine a non-empty list overlay with the authoritative per-defect rows.
  useEffect(() => {
    const overlayIsAuthoritativeEmpty =
      projectedErtsOverlay !== undefined && Object.keys(projectedErtsOverlay).length === 0;
    if (!isOpen || !event?.eventId || overlayIsAuthoritativeEmpty) return;
    let cancelled = false;
    const defectIds: number[] =
      event.defects && event.defects.length > 0 ? event.defects.map((defect) => defect.id) : [1];

    fetchErtsPrediction(event.eventId).then((prediction: ErtsPrediction | null): void => {
      if (cancelled || !prediction) return;
      setProjectedErts((previous: Record<number, string>): Record<number, string> =>
        mergeProjection(previous, prediction, defectIds),
      );
    });

    return (): void => {
      cancelled = true;
    };
  }, [isOpen, event?.eventId, event?.defects, projectedErtsOverlay]);

  return { projectedErts, setProjectedErts };
}
